import React, { useState, useEffect } from 'react';

interface AppStoreStatsData {
  appInfo: any;
  versions: any[];
  reviews: {
    recent: any[];
    stats: {
      total: number;
      averageRating: number;
      ratingDistribution: Record<number, number>;
    };
  };
  pricing: any[];
  downloads?: {
    totalDownloads: number;
    downloadsByDate: Array<{ date: string; downloads: number }>;
    trend: 'up' | 'down' | 'stable';
    percentageChange: number;
  };
}

/**
 * Component for displaying App Store production statistics
 * Shows app info, versions, reviews, and other production metrics
 */
export const AppStoreStats: React.FC = () => {
  const [stats, setStats] = useState<AppStoreStatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<{ success: boolean; message: string } | null>(null);

  // Fetch App Store stats
  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/appstore/stats', {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch App Store stats: ${response.status}`);
      }

      const data = await response.json();
      console.log('App Store stats:', data);
      setStats(data.stats);
    } catch (err) {
      console.error('Error fetching App Store stats:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch App Store statistics');
    } finally {
      setLoading(false);
    }
  };

  // Test App Store Connect connection
  const testConnection = async () => {
    setTestingConnection(true);
    setConnectionStatus(null);
    
    try {
      const response = await fetch('/api/appstore/test-connection', {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      setConnectionStatus(data);
      
      if (data.success) {
        // If connection is successful, fetch stats
        fetchStats();
      }
    } catch (err) {
      console.error('Error testing connection:', err);
      setConnectionStatus({
        success: false,
        message: err instanceof Error ? err.message : 'Connection test failed'
      });
    } finally {
      setTestingConnection(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  // Render star rating
  const renderStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const stars = [];

    for (let i = 0; i < fullStars; i++) {
      stars.push(<span key={`full-${i}`} className="text-yellow-400">★</span>);
    }
    if (hasHalfStar) {
      stars.push(<span key="half" className="text-yellow-400">⯨</span>);
    }
    for (let i = stars.length; i < 5; i++) {
      stars.push(<span key={`empty-${i}`} className="text-gray-300">☆</span>);
    }

    return <div className="flex items-center gap-1">{stars}</div>;
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-gray-600">Loading App Store statistics...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading App Store statistics</h3>
              <p className="mt-2 text-sm text-red-700">{error}</p>
              <div className="mt-4 flex gap-2">
                <button
                  onClick={fetchStats}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                >
                  Retry
                </button>
                <button
                  onClick={testConnection}
                  disabled={testingConnection}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {testingConnection ? 'Testing...' : 'Test Connection'}
                </button>
              </div>
              {connectionStatus && (
                <div className={`mt-3 p-3 rounded ${connectionStatus.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {connectionStatus.message}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-500">No App Store statistics available</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">App Store Statistics</h2>
          <p className="text-sm text-gray-500 mt-1">Production app metrics and analytics</p>
        </div>
        <button
          onClick={fetchStats}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* App Info */}
      {stats.appInfo && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">App Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">App Name</p>
              <p className="text-base font-medium text-gray-900">{stats.appInfo.attributes?.name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Bundle ID</p>
              <p className="text-base font-medium text-gray-900">{stats.appInfo.attributes?.bundleId || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">SKU</p>
              <p className="text-base font-medium text-gray-900">{stats.appInfo.attributes?.sku || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Primary Locale</p>
              <p className="text-base font-medium text-gray-900">{stats.appInfo.attributes?.primaryLocale || 'N/A'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Versions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">App Store Versions</h3>
        {stats.versions && stats.versions.length > 0 ? (
          <div className="space-y-3">
            {stats.versions.map((version: any) => (
              <div key={version.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-base font-semibold text-gray-900">
                      Version {version.attributes?.versionString || 'N/A'}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      Status: <span className="font-medium">{version.attributes?.appStoreState || 'Unknown'}</span>
                    </p>
                    {version.attributes?.releaseType && (
                      <p className="text-sm text-gray-500">
                        Release Type: <span className="font-medium">{version.attributes.releaseType}</span>
                      </p>
                    )}
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    version.attributes?.appStoreState === 'READY_FOR_SALE' 
                      ? 'bg-green-100 text-green-800'
                      : version.attributes?.appStoreState === 'PROCESSING_FOR_APP_STORE'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {version.attributes?.appStoreState || 'Unknown'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-sm">No versions found</p>
        )}
      </div>

      {/* Reviews Statistics */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Reviews</h3>
        
        {/* Review Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="text-center">
            <p className="text-sm text-gray-500 mb-2">Average Rating</p>
            <div className="flex flex-col items-center">
              <p className="text-3xl font-bold text-gray-900">{stats.reviews.stats.averageRating.toFixed(2)}</p>
              {renderStars(stats.reviews.stats.averageRating)}
            </div>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-500 mb-2">Total Reviews</p>
            <p className="text-3xl font-bold text-gray-900">{stats.reviews.stats.total}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-2">Rating Distribution</p>
            <div className="space-y-1">
              {[5, 4, 3, 2, 1].map(rating => {
                const count = stats.reviews.stats.ratingDistribution[rating] || 0;
                const percentage = stats.reviews.stats.total > 0 
                  ? (count / stats.reviews.stats.total * 100).toFixed(0) 
                  : 0;
                return (
                  <div key={rating} className="flex items-center gap-2 text-sm">
                    <span className="w-3 text-gray-600">{rating}</span>
                    <span className="text-yellow-400">★</span>
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-yellow-400 h-2 rounded-full" 
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <span className="w-12 text-right text-gray-600">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Recent Reviews */}
        {stats.reviews.recent && stats.reviews.recent.length > 0 && (
          <div>
            <h4 className="text-base font-semibold text-gray-900 mb-3">Recent Reviews</h4>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {stats.reviews.recent.slice(0, 10).map((review: any) => (
                <div key={review.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      {renderStars(review.attributes?.rating || 0)}
                      <span className="text-sm text-gray-500">
                        {new Date(review.attributes?.createdDate).toLocaleDateString()}
                      </span>
                    </div>
                    {review.attributes?.territory && (
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                        {review.attributes.territory}
                      </span>
                    )}
                  </div>
                  {review.attributes?.title && (
                    <p className="font-medium text-gray-900 mb-1">{review.attributes.title}</p>
                  )}
                  {review.attributes?.body && (
                    <p className="text-sm text-gray-600">{review.attributes.body}</p>
                  )}
                  {review.attributes?.reviewerNickname && (
                    <p className="text-xs text-gray-500 mt-2">
                      by {review.attributes.reviewerNickname}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Downloads & Trends */}
      {stats.downloads && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Downloads & Trends</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-2">Total Downloads (30 days)</p>
              <p className="text-3xl font-bold text-gray-900">
                {stats.downloads.totalDownloads.toLocaleString()}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-2">Trend</p>
              <div className="flex items-center justify-center gap-2">
                {stats.downloads.trend === 'up' && (
                  <>
                    <span className="text-green-500 text-2xl">↗</span>
                    <span className="text-green-600 font-semibold">Up</span>
                  </>
                )}
                {stats.downloads.trend === 'down' && (
                  <>
                    <span className="text-red-500 text-2xl">↘</span>
                    <span className="text-red-600 font-semibold">Down</span>
                  </>
                )}
                {stats.downloads.trend === 'stable' && (
                  <>
                    <span className="text-gray-500 text-2xl">→</span>
                    <span className="text-gray-600 font-semibold">Stable</span>
                  </>
                )}
              </div>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-2">Change</p>
              <p className={`text-2xl font-bold ${
                stats.downloads.percentageChange > 0 
                  ? 'text-green-600' 
                  : stats.downloads.percentageChange < 0 
                  ? 'text-red-600' 
                  : 'text-gray-600'
              }`}>
                {stats.downloads.percentageChange > 0 ? '+' : ''}
                {stats.downloads.percentageChange.toFixed(1)}%
              </p>
            </div>
          </div>

          {/* Downloads Chart */}
          {stats.downloads.downloadsByDate && stats.downloads.downloadsByDate.length > 0 && (() => {
            const downloadsByDate = stats.downloads.downloadsByDate;
            return (
              <div>
                <h4 className="text-base font-semibold text-gray-900 mb-3">Daily Downloads</h4>
                <div className="space-y-2">
                  {downloadsByDate.slice(-14).map((day: any) => {
                    const maxDownloads = Math.max(...downloadsByDate.map((d: any) => d.downloads));
                  const percentage = maxDownloads > 0 ? (day.downloads / maxDownloads) * 100 : 0;
                  
                  return (
                    <div key={day.date} className="flex items-center gap-3">
                      <span className="text-xs text-gray-600 w-24">
                        {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                      <div className="flex-1 bg-gray-200 rounded-full h-4 relative">
                        <div 
                          className="bg-blue-500 h-4 rounded-full flex items-center justify-end pr-2"
                          style={{ width: `${percentage}%` }}
                        >
                          {day.downloads > 0 && percentage > 15 && (
                            <span className="text-xs text-white font-medium">{day.downloads}</span>
                          )}
                        </div>
                      </div>
                      <span className="text-sm font-medium text-gray-900 w-16 text-right">
                        {day.downloads.toLocaleString()}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            );
          })()}

          {(!stats.downloads.downloadsByDate || stats.downloads.downloadsByDate.length === 0) && (
            <div className="text-center py-8 text-gray-500">
              <p className="text-sm">Download data not available</p>
              <p className="text-xs mt-2">Analytics API access may be required</p>
            </div>
          )}
        </div>
      )}

      {/* Pricing Info */}
      {stats.pricing && stats.pricing.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Pricing Information</h3>
          <p className="text-sm text-gray-500">
            {stats.pricing.length} price point{stats.pricing.length !== 1 ? 's' : ''} configured
          </p>
        </div>
      )}
    </div>
  );
};

export default AppStoreStats;

